import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cx } from "./ui/cx";

/**
 * Thumb-reach bottom navigation for field officers on phones. Renders only
 * under the `sm` breakpoint so desktop / drawer nav is unaffected.
 *
 * The 5 destinations match a beat constable's main daily flow:
 *   Cases · Capture · Diary · Inbox · Me
 *
 * The middle slot is a primary FAB-style "Capture" action that opens the
 * voice complaint bot — the most common in-field action.
 */
const items = [
  { to: "/my-cases",          icon: "📁",  label: "Cases",   key: "cases" },
  { to: "/case-diary",        icon: "📔", label: "Diary",   key: "diary" },
  { to: "/complaints/voice",  icon: "🎙",  label: "Capture", key: "capture", primary: true },
  { to: "/alerts",            icon: "🔔", label: "Inbox",   key: "inbox" },
  { to: "/me",                icon: "👤", label: "Me",      key: "me" },
];

export function FieldBottomNav() {
  const { t } = useTranslation("common");
  const labels: Record<string, string> = {
    cases:   "Cases",
    diary:   "Diary",
    capture: t("actions.search") === t("actions.search") ? "Capture" : "Capture", // placeholder for future
    inbox:   "Inbox",
    me:      t("nav.profile"),
  };

  return (
    <nav
      role="navigation"
      aria-label="Field officer quick nav"
      className="fixed bottom-0 left-0 right-0 z-nav border-t border-default bg-[var(--c-surface)]/95 backdrop-blur shadow-modal sm:hidden no-print"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-5 gap-1 px-1 py-1.5">
        {items.map((it) => (
          <li key={it.key} className="contents">
            <NavLink
              to={it.to}
              end
              className={({ isActive }) => cx(
                "tap flex flex-col items-center justify-center rounded-md px-1 text-[10px] focus-ring",
                it.primary
                  ? "bg-[var(--c-brand)] text-white -translate-y-2 h-12 w-12 shadow-modal rounded-full m-auto"
                  : isActive
                    ? "bg-[var(--c-brand-soft)] text-[var(--c-brand-strong)] font-semibold"
                    : "text-muted hover:text-default"
              )}
            >
              <span aria-hidden className={cx("text-lg leading-none", it.primary && "text-xl")}>{it.icon}</span>
              {!it.primary && <span className="mt-0.5">{labels[it.key]}</span>}
              {it.primary && <span className="sr-only">{labels[it.key]}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
